import Datepicker from '@/components/ui/daterange';
import { Vessel, VoyagerPlanner } from '@/types/api';
import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';

import usePlannerStore from '@/store/voyager';
import { VoyagerFormSearch } from '@/types/component';

import CheckIcon from '@/assets/icons/check.svg';
import CaretDownIcon from '@/assets/icons/caret-down.svg';
import { useQueryAllPlanners } from '@/api/queries/voyagers';
import toast from 'react-hot-toast';
import Button from '@/components/ui/button';
import { Search } from 'lucide-react';

type Props = {
  onSearch: Function;
};

export default function FormSearchVoyager({ onSearch }: Props) {
  const usePlanner = usePlannerStore((state) => state);
  const { voyagerPlanners, setPlanners } = usePlanner;

  const {
    isError: isErrorPlanner,
    data: dataPlanner,
    isFetched: isFetchedPlanner,
  } = useQueryAllPlanners();

  useEffect(() => {
    if (dataPlanner) {
      setPlanners(dataPlanner);
    }
    if (isErrorPlanner) {
      toast.error(
        'Terjadi kesalahan pada pengambilan data planner voyager, harap hubungi Administrator',
      );
    }
  }, [dataPlanner, isFetchedPlanner]);

  const [selected, setSelected] = useState<VoyagerPlanner | null>(null);
  const [query, setQuery] = useState('');

  const filteredVoyagers =
    query === ''
      ? voyagerPlanners.filter((val) =>
          val.name.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')),
        )
      : voyagerPlanners;

  const onButtonSearch = () => {
    const voyagerFormData: VoyagerFormSearch = {
      voyager: selected || null,
    };
    onSearch(voyagerFormData);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 relative mt-6 mb-6">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500 rounded-l-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end pl-2">
        <div className="block">
          <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Voyager Planner</span>
          <Combobox value={selected} onChange={setSelected}>
            {({ open }) => (
              <div className="relative">
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery('')}
                >
                  <Combobox.Options
                    className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white dark:bg-gray-700 py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-[999]"
                  >
                    {filteredVoyagers?.length === 0 && query !== '' ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700 dark:text-white">
                        Nothing found.
                      </div>
                    ) : (
                      filteredVoyagers.map((voyager: VoyagerPlanner, key) => (
                        <Combobox.Option
                          key={key}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-brand-500 text-white' : 'text-gray-900 dark:text-white'
                            }`
                          }
                          value={voyager}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? 'font-medium' : 'font-normal'
                                }`}
                              >
                                {voyager?.name}
                              </span>
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-brand-500 dark:text-white'
                                  }`}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Transition>

                <div className="relative w-full cursor-default overflow-hidden text-left focus:outline-none focus-visible:ring-2 sm:text-sm">
                  <Combobox.Button as="div" className="w-full">
                    <Combobox.Input
                      className="h-10 w-full pl-3 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                      displayValue={(voyager: VoyagerPlanner) => voyager?.name}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search Voyager Name"
                      autoComplete="off"
                    />
                  </Combobox.Button>
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <CaretDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                </div>
              </div>
            )}
          </Combobox>
        </div>

        <button
          type="button"
          onClick={onButtonSearch}
          className="h-10 w-full md:w-auto px-6 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:opacity-95 shadow-sm inline-flex items-center justify-center gap-2 transition-all"
        >
          <Search className="h-4 w-4" /> Search
        </button>
      </div>
    </div>
  );
}
